import React, { useState, useRef } from 'react';
import { FileText, ClipboardCheck, FileCheck, Send, Upload, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import type { FormData } from '../types/form';
import { sendToWebhook } from '../lib/webhook';
import { SEO } from '../lib/seo';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const BRIE: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    billImage: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fileError, setFileError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submittedData, setSubmittedData] = useState<FormData | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFileError('');
    setSubmitError('');

    try {
      if (!formData.billImage) {
        setFileError(t('form.fileUpload.required'));
        return;
      }

      if (formData.billImage.size > MAX_FILE_SIZE) {
        setFileError(t('form.fileUpload.sizeError'));
        return;
      }

      await sendToWebhook(formData, 'BRIE');
      
      setSubmittedData(formData);
      setSubmitted(true);
      setFormData({
        name: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        billImage: null
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('form.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setFileError(t('form.fileUpload.required'));
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heif', 'image/heic'];
    if (!allowedTypes.includes(file.type)) {
      setFileError(t('form.fileUpload.error'));
      e.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError(t('form.fileUpload.sizeError'));
      e.target.value = '';
      return;
    }

    setFileError('');
    setFormData(prev => ({
      ...prev,
      billImage: file
    }));
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      setSubmittedData(null);
    }, 300);
  };

  return (
    <>
      <SEO 
        title="Boletín BRIE Barcelona | Reconocimiento Instalaciones Eléctricas"
        description="Obtén tu Boletín de Reconocimiento de Instalaciones Eléctricas (BRIE) en Barcelona. Análisis técnico, informe completo y evaluación de seguridad."
        canonical="/brie"
        type="website"
      />
      
      <div className="py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-8">{t('briePage.title')}</h1>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-50 p-6 rounded-lg">
              <FileText className="w-12 h-12 text-yellow-500 mb-4" />
              <h3 className="text-xl font-bold mb-2">{t('briePage.features.analysis.title')}</h3>
              <p>{t('briePage.features.analysis.description')}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <ClipboardCheck className="w-12 h-12 text-yellow-500 mb-4" />
              <h3 className="text-xl font-bold mb-2">{t('briePage.features.report.title')}</h3>
              <p>{t('briePage.features.report.description')}</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <FileCheck className="w-12 h-12 text-yellow-500 mb-4" />
              <h3 className="text-xl font-bold mb-2">{t('briePage.features.assessment.title')}</h3>
              <p>{t('briePage.features.assessment.description')}</p>
            </div>
          </div>

          <div className="prose max-w-none">
            <div className="bg-yellow-50 p-8 rounded-lg mb-12">
              <h2 className="text-3xl font-bold mb-6">{t('briePage.process.title')}</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('briePage.process.steps.inspection.title')}</h3>
                  <p>{t('briePage.process.steps.inspection.description')}</p>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('briePage.process.steps.analysis.title')}</h3>
                  <p>{t('briePage.process.steps.analysis.description')}</p>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('briePage.process.steps.documentation.title')}</h3>
                  <p>{t('briePage.process.steps.documentation.description')}</p>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{t('briePage.process.steps.report.title')}</h3>
                  <p>{t('briePage.process.steps.report.description')}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => setIsOpen(true)}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 px-8 rounded-full transition-all duration-200 transform hover:scale-105 shadow-lg flex items-center gap-2"
              >
                <FileCheck className="w-5 h-5" />
                {t('form.requestBRIE')}
              </button>
            </div>
          </div>
        </div>

        <Transition appear show={isOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={handleClose}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-yellow-50 p-6 shadow-xl transition-all">
                    <Dialog.Title as="h3" className="text-2xl font-bold mb-6">
                      {t('form.requestBRIE')}
                    </Dialog.Title>

                    {submitted ? (
                      <div className="space-y-4">
                        <div className="bg-green-50 p-6 rounded-lg">
                          <div className="flex items-center gap-2 text-green-600 mb-4">
                            <CheckCircle className="w-6 h-6" />
                            <span className="font-semibold">{t('form.thankYou')}</span>
                          </div>
                          {submittedData && (
                            <div className="space-y-2 text-gray-600">
                              <p><span className="font-semibold">{t('form.name')}:</span> {submittedData.name}</p>
                              <p><span className="font-semibold">{t('form.lastName')}:</span> {submittedData.lastName}</p>
                              <p><span className="font-semibold">{t('form.email')}:</span> {submittedData.email}</p>
                              <p><span className="font-semibold">{t('form.phone')}:</span> {submittedData.phone}</p>
                              <p><span className="font-semibold">{t('form.address')}:</span> {submittedData.address}</p>
                              {submittedData.billImage && (
                                <p><span className="font-semibold">{t('form.electricityBill')}:</span> {submittedData.billImage.name}</p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                          >
                            {t('form.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                        {submitError && (
                          <div className="text-red-600 p-4 bg-red-50 rounded-lg">
                            {submitError}
                          </div>
                        )}
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                              {t('form.name')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              required
                              value={formData.name}
                              onChange={handleChange}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                            />
                          </div>

                          <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                              {t('form.lastName')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="lastName"
                              name="lastName"
                              required
                              value={formData.lastName}
                              onChange={handleChange}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                            />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                              {t('form.email')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              required
                              value={formData.email}
                              onChange={handleChange}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                            />
                          </div>

                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                              {t('form.phone')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="tel"
                              id="phone"
                              name="phone"
                              required
                              value={formData.phone}
                              onChange={handleChange}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                            {t('form.address')} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="address"
                            name="address"
                            required
                            value={formData.address}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('form.electricityBill')} <span className="text-red-500">*</span>
                          </label>
                          <div className="flex items-center justify-center w-full">
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-gray-50">
                              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <Upload className="w-8 h-8 mb-2 text-gray-500" />
                                <p className="mb-2 text-sm text-gray-500">
                                  <span className="font-semibold">{t('form.fileUpload.click')}</span> {t('form.fileUpload.dragDrop')}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {t('form.fileUpload.allowedFormats')}
                                </p>
                              </div>
                              <input
                                type="file"
                                className="hidden"
                                accept=".jpeg,.jpg,.png,.heif,.heic"
                                onChange={handleFileChange}
                                required
                                ref={fileInputRef}
                              />
                            </label>
                          </div>
                          {formData.billImage && (
                            <p className="mt-2 text-sm text-gray-600">
                              {t('form.fileUpload.selectedFile')} {formData.billImage.name}
                            </p>
                          )}
                          {fileError && (
                            <div className="text-red-600 text-sm mt-2">
                              {fileError}
                            </div>
                          )}
                        </div>

                        <div className="flex justify-end gap-4 mt-6">
                          <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                          >
                            {t('form.cancel')}
                          </button>
                          <button
                            type="submit"
                            disabled={submitting}
                            className="flex justify-center items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {submitting ? (
                              t('form.sending')
                            ) : (
                              <>
                                <Send className="w-5 h-5" />
                                {t('form.requestBRIE')}
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </>
  );
};

export default BRIE;